import React, { useMemo } from 'react';
import { HolidayActivityFill } from "./WallPlannerActivityBlocks";
import { daysBetween } from '@shared/utils/DateUtils';
import moment, { Moment } from "moment";
import { HolidayDay } from "../../../../components/pages/holidays-v2/models/Holidays.model";
import { HolidayInfoAndApprovalPopup, PopupPosition } from "./HolidayInfoAndApprovalPopup";
import { BasicUserInfo, Birthday } from '@shared/models/user.models';
import { ApprovalState } from '@features/holidays/models/approval-requests.models';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import { useFillHover } from "./useFillHover";

function holidayBlockOutlineColour(state: ApprovalState) {
  switch (state) {
    case ApprovalState.approved: return Colours.outlineBlue;
    case ApprovalState.pending: return Colours.orange;
    default: return '#A6B4B4';
  }
}

function holidayBlockFillColour(state: ApprovalState) {
  switch (state) {
    case ApprovalState.approved: return Colours.blue;
    case ApprovalState.pending: return Colours.orange50;
    default: return Colours.veryLightGrey;
  }
}

function holidayBlockTextColour(state: ApprovalState) {
  switch (state) {
    case ApprovalState.approved: return Colours.white;
    default: return Colours.black;
  }
}

export function HolidayBlock(props: Props) {
  const {activity, firstDateOnCalendar} = props;
  const hover = useFillHover();

  const days = useMemo(() => daysBetween(activity.toDate, activity.fromDate) + 1, [activity]);

  const startsWithHalfDay = useMemo(() => {
    if (!activity.holidayBookingList || activity.holidayBookingList.length === 0) return false;
    let startDay = activity.holidayBookingList[0];
    activity.holidayBookingList.forEach(((bookingDay: HolidayDay) => {
      if (bookingDay.date < startDay.date) startDay = bookingDay;
    }));
    return !startDay.am;
  }, [activity]);

  const endsWithHalfDay = useMemo(() => {
    if (!activity.holidayBookingList || activity.holidayBookingList.length === 0) return false;
    let endDay = activity.holidayBookingList[0];
    activity.holidayBookingList.forEach(((bookingDay: HolidayDay) => {
      if (bookingDay.date > endDay.date) endDay = bookingDay;
    }));
    return !endDay.pm;
  }, [activity]);

  const popupPositioning = useMemo(() => {
    const daysDiff = moment(activity.fromDate).diff(firstDateOnCalendar, 'days');
    return daysDiff > 20 ? PopupPosition.Right : PopupPosition.Left;
  }, [activity, firstDateOnCalendar]);

  const holidayState = useMemo(() => {
    const bookingList = activity.holidayBookingList ?? [];
    const pending = bookingList.filter((booking: HolidayDay) => booking.state === ApprovalState.pending);
    const declined = bookingList.filter((booking: HolidayDay) => booking.state === ApprovalState.declined);

    if (declined.length > 0) return ApprovalState.declined;
    else if (pending.length > 0) return ApprovalState.pending;
    else return ApprovalState.approved;
  }, [activity]);

  return (
    <HolidayActivityFill ref={hover.ref}
                         start={daysBetween(activity.fromDate, firstDateOnCalendar)}
                         days={days}
                         startsWithHalfDay={startsWithHalfDay}
                         endsWithHalfDay={endsWithHalfDay}
                         fill={holidayBlockFillColour(holidayState)}
                         outline={holidayBlockOutlineColour(holidayState)}
                         textColour={holidayBlockTextColour(holidayState)}
                         {...hover.triggerProps}
                         title={activity?.name ?? 'Holiday'}>
      <Contents>
        {days === 1 && (endsWithHalfDay || startsWithHalfDay) ? <></> : <Icon icon={IconTypes.Holiday} />}
        {props.children && <>{props.children}</>}
      </Contents>
      <HolidayInfoAndApprovalPopup activity={activity}
                                   popupPositioning={popupPositioning}
                                   isVisible={hover.visible}
                                   triggerRect={hover.rect}
                                   onMouseEnter={hover.popupProps.onMouseEnter}
                                   onMouseLeave={hover.popupProps.onMouseLeave}
                                   user={props.user} />
    </HolidayActivityFill>
  )
}

interface Props {
  activity: any;
  children?: any;
  user?: BasicUserInfo;
  firstDateOnCalendar: Moment;
  weeksInMonth: number;
  birthday?: Birthday;
}

const Contents = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
`
